import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Box, Typography, Tab, Tabs} from '@mui/material';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import AssignmentPanel, {
  BulkAssignSamplingOption,
} from '../assignment-panel/AssignmentPanel';
import {t} from 'i18next';
import './BulkAssign.scss';
import {
  IAssignmentGroupNode,
  ICompletedAssignmentTree,
} from '../../../../api/types/assignment.type';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {
  useAssignmentTree,
  useCompleteAssignmentTree,
} from '../../../../hooks/assignments/useAssignments';
import {ISelectItem} from '../../../../shared/types';
import {ASSIGNMENT_TYPES} from '../../../../shared/constants';

export enum BulkAssignActionOption {
  AssignDoc = 'assign_documents',
  UnassignDoc = 'unassign_documents',
}
// Types Definitions
// ----------------------------------
export interface IOptions {
  label: string;
  value: boolean;
}
// ------------------

export interface IActions {
  label: string;
  value: string;
}
// ------------------

interface ITabOption {
  id: string;
  label: string;
  content: string | ReactElement;
}
// ------------------

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
// ------------------

interface IBulkAssignProps {
  selectedIds: string[];
  onActionChange?: (value: string) => void;
  onTabChange?: (newTabValue: number) => void;
  onError?: (errorMessage: string | null) => void;
  setSelectedAction: React.Dispatch<
    React.SetStateAction<'assign_documents' | 'unassign_documents'>
  >;
  startingCount: number;
  isStartingCountLoading?: boolean;
  onTreeSelect?: (ids: string[] | null) => void;
  onOptionChange?: (values: {
    samplingMethod: string;
    persistContent: boolean;
    count: number;
    percent: number | undefined;
  }) => void;
  initialSelectedAction?: BulkAssignActionOption;
}

const enBulkAssign = 'actionMenu.bulkAssignFlyout';

// ----------------------------------
// TabPanel Component
// ----------------------------------
function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`bulk-assign-tab-panel-${index}`}
    >
      {value === index && (
        <div data-testid={`bulk-assign-tag-panel-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

type TreeItemsMap = {
  assign: string[];
  unAssign: string[];
  newAssign: string[];
};
enum BulkAssignTabOption {
  ExistingAssign = 0,
  NewAssign = 1,
}

export type AssignState = {
  samplingMethod: ISelectItem;
  numberToAssign: string;
  percentToAssign: string;
  persistContentHits: boolean;
};
type AssignDataMap = {
  existingAssign: AssignState | null;
  newAssign: AssignState | null;
};
type AssignTabMap = {
  assignment: BulkAssignTabOption;
  unAssignment: BulkAssignTabOption;
};

const getAssignmentDefaultState = (): AssignState => {
  return {
    samplingMethod: {
      key: BulkAssignSamplingOption.Count,
      id: BulkAssignSamplingOption.Count,
      name: t(`${enBulkAssign}.samplingOptions.selectedDocumentsCount`),
    },
    numberToAssign: '',
    percentToAssign: '',
    persistContentHits: false,
  };
};

const ASSIGNMENT_DELAY = 1000;

// ----------------------------------
// BulkAssign Component
// ----------------------------------
const BulkAssign: React.FC<IBulkAssignProps> = ({
  onActionChange,
  onTabChange,
  onError,
  setSelectedAction,
  startingCount,
  isStartingCountLoading = true,
  onTreeSelect,
  onOptionChange,
  initialSelectedAction,
}) => {
  const [actionValue, setActionValue] = useState<BulkAssignActionOption>(
    initialSelectedAction ?? BulkAssignActionOption.AssignDoc
  );
  const [tabValue, setTabValue] = useState<number>(0);
  const [showDocumentCount, setShowDocumentCount] = useState<number>(0);
  const [bulkAssignStateMap, setBulkAssignStateMap] = useState<AssignDataMap>({
    existingAssign: getAssignmentDefaultState(),
    newAssign: getAssignmentDefaultState(),
  });
  const [tabSelectionMap, setTabSelectionMap] = useState<AssignTabMap>({
    assignment: BulkAssignTabOption.ExistingAssign,
    unAssignment: BulkAssignTabOption.ExistingAssign,
  });
  const [selectedTreeItemsMap, setSelectedTreeItemsMap] =
    useState<TreeItemsMap>({assign: [], newAssign: [], unAssign: []});
  const [countLoading, setCountLoading] = useState<boolean>(true);

  useEffect(() => {
    if (actionValue == BulkAssignActionOption.AssignDoc) {
      const assignData = bulkAssignStateMap.existingAssign;
      const newAssignData = bulkAssignStateMap.newAssign;
      if (tabValue == BulkAssignTabOption.ExistingAssign && assignData) {
        onOptionChange?.({
          samplingMethod: assignData.samplingMethod.key?.toString() ?? '',
          count: +assignData.numberToAssign,
          percent: +assignData.percentToAssign,
          persistContent: assignData.persistContentHits,
        });
      } else if (tabValue == BulkAssignTabOption.NewAssign && newAssignData) {
        onOptionChange?.({
          samplingMethod: newAssignData.samplingMethod.key?.toString() ?? '',
          count: +newAssignData.numberToAssign,
          percent: +newAssignData.percentToAssign,
          persistContent: newAssignData.persistContentHits,
        });
      }
    }
  }, [bulkAssignStateMap, tabValue]);

  // Tab Options
  // ----------------------------------
  const actions: IActions[] = useMemo(
    () => [
      {
        label: t(`${enBulkAssign}.assignDocuments`),
        value: BulkAssignActionOption.AssignDoc,
      },
      {
        label: t(`${enBulkAssign}.unassignDocuments`),
        value: BulkAssignActionOption.UnassignDoc,
      },
    ],
    []
  );
  // ------------------

  const {data: assgnTreeData} = useAssignmentTree(0) as {
    data?: IAssignmentGroupNode[];
  };
  const {data: completeTreeData} = useCompleteAssignmentTree(
    showDocumentCount
  ) as {
    data?: ICompletedAssignmentTree[];
  };
  const {data: assignCompleteTreeData} = useCompleteAssignmentTree(0) as {
    data?: ICompletedAssignmentTree[];
  };

  const mergeAndTransformToTree = (
    groupData: IAssignmentGroupNode[] = [],
    assignmentData: ICompletedAssignmentTree[] = []
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const groupMap = new Map(groupData.map((g) => [String(g.id), g]));
    const assignmentMap = new Map(assignmentData.map((a) => [String(a.id), a]));
    const validGroupIds = new Set<string>(
      groupData.map((group) => String(group.id))
    );
    validGroupIds.add('#');

    // Add groups to nodeMap
    for (const group of groupData) {
      nodeMap.set(group.id, {
        id: group.id,
        fileType: 'folder',
        label: group.text,
        isGroup: group.isGroup,
        type: group.type || 'AssignmentGroup',
        count: group.documentCount?.toString() || '',
        isCascadeEnabled: group.li_attr?.isCascadeEnabled,
        showMoreOptions: true,
        children: [],
        // Add other ExtendedTreeItemProps fields if needed here...
      });
    }

    // Add assignments to nodeMap
    for (const assignment of assignmentData) {
      nodeMap.set(assignment.id.toString(), {
        id: assignment.id.toString(),
        label: assignment.text,
        isGroup: false,
        fileType: 'folder',
        count: '',
        type: assignment.type || 'Assignment',
        isCascadeEnabled: false,
        showMoreOptions: true,
        children: [],
        // Add other ExtendedTreeItemProps fields if needed here...
      });
    }

    // Build tree structure
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    for (const node of nodeMap.values()) {
      const groupMatch = groupMap.get(node.id);
      const assignmentMatch = assignmentMap.get(node.id);

      const parentId = String(
        groupMatch?.parent ?? assignmentMatch?.parent ?? '#'
      );
      const isValidParent = validGroupIds.has(parentId);
      const isRootParent = parentId === '#';

      if (assignmentMatch && !isValidParent && !isRootParent) {
        continue;
      }

      if (isValidParent && !isRootParent) {
        const parentNode = nodeMap.get(parentId);
        if (parentNode) {
          parentNode.children = parentNode.children ?? [];
          parentNode.children.push(node);
        } else if (groupMatch) {
          tree.push(node);
        }
      } else if (isRootParent || groupMatch) {
        tree.push(node);
      }
    }
    // Sort the tree nodes alphabetically by the 'text' property (group.text or assignment.text)
    tree.sort((a, b) => a.label.localeCompare(b.label));

    // Sort the children of each node alphabetically by the 'text' property without using forEach
    const sortChildren = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      return nodes
        .map((node) => {
          // Recursively sort the children and create a new node with sorted children
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: sortChildren(node.children), // Recursively sort children
            };
          }
          return node;
        })
        .sort((a, b) => a.label.localeCompare(b.label)); // Sort the nodes themselves by 'label'
    };

    return sortChildren(tree); // Sort children and root tree nodes
  };

  const nestedUnassignTreeData = useMemo(
    () => mergeAndTransformToTree(assgnTreeData, completeTreeData),
    [assgnTreeData, completeTreeData]
  );

  const nestedAssignTreeData = useMemo(
    () => mergeAndTransformToTree(assgnTreeData, assignCompleteTreeData),
    [assgnTreeData, assignCompleteTreeData]
  );

  const filterAssignmentsOnly = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes
      .map((node) => {
        if (node.children) {
          const filteredChildren = filterAssignmentsOnly(node.children);

          return {
            ...node,
            children: filteredChildren.toSorted((a, b) =>
              a.id.localeCompare(b.id)
            ),
          };
        }

        return node;
      })
      .filter(
        (node) =>
          node.type?.toLowerCase() === ASSIGNMENT_TYPES.GROUP ||
          node.type?.toLowerCase() === ASSIGNMENT_TYPES.DEFAULT ||
          (node.children && node.children.length > 0)
      )
      .toSorted((a, b) => a.id.localeCompare(b.id)); // use toSorted here too
  };

  const newAssignmentsTreeData = useMemo(
    () => filterAssignmentsOnly(nestedAssignTreeData),
    [nestedAssignTreeData]
  );

  const treeItemSelectionHandler = useCallback(
    (items: string[]) => {
      onTreeSelect?.(items);
      if (actionValue == BulkAssignActionOption.AssignDoc) {
        if (tabValue == BulkAssignTabOption.ExistingAssign) {
          setSelectedTreeItemsMap((pv) => {
            return {...pv, assign: items};
          });
        } else {
          setSelectedTreeItemsMap((pv) => {
            return {...pv, newAssign: items};
          });
        }
      } else {
        setSelectedTreeItemsMap((pv) => {
          return {...pv, unAssign: items};
        });
      }
    },
    [tabValue, actionValue, selectedTreeItemsMap]
  );

  const assignmentDataChangeHandler = useCallback(
    (assignmentData: AssignState) => {
      if (tabValue == BulkAssignTabOption.ExistingAssign) {
        setBulkAssignStateMap((pv) => {
          return {...pv, existingAssign: assignmentData};
        });
      } else {
        setBulkAssignStateMap((pv) => {
          return {...pv, newAssign: assignmentData};
        });
      }
    },
    [actionValue, tabValue]
  );

  const getTabOptions = useCallback((): ITabOption[] => {
    return [
      {
        id: 'existing_assignments',
        label: t(`${enBulkAssign}.existingAssignments`),
        content: (
          <AssignmentPanel
            selectedTreeItems={
              actionValue == BulkAssignActionOption.AssignDoc
                ? selectedTreeItemsMap.assign
                : selectedTreeItemsMap.unAssign
            }
            data-testid="existing-assignment-content"
            treeData={nestedAssignTreeData}
            unAssignTreeData={nestedUnassignTreeData}
            startingCount={startingCount}
            isStartingCountLoading={countLoading}
            assignmentType="existing"
            actionType={actionValue}
            showDocumentCount={showDocumentCount}
            setShowDocumentCount={setShowDocumentCount}
            onTreeSelect={(ids) => {
              treeItemSelectionHandler(ids ?? []);
            }}
            onError={(errorMessage) => {
              if (onError) onError(errorMessage);
            }}
            assignmentData={
              actionValue == BulkAssignActionOption.AssignDoc
                ? bulkAssignStateMap.existingAssign
                : null
            }
            onAssignmentDataChange={assignmentDataChangeHandler}
          />
        ),
      },
      {
        id: 'new_assignments',
        label: t(`${enBulkAssign}.newAssignments`),
        content: (
          <AssignmentPanel
            selectedTreeItems={selectedTreeItemsMap.newAssign}
            data-testid="new-assignment-content"
            treeData={newAssignmentsTreeData}
            startingCount={startingCount}
            isStartingCountLoading={countLoading}
            assignmentType="new"
            actionType={actionValue}
            onTreeSelect={(ids) => {
              treeItemSelectionHandler(ids ?? []);
            }}
            onError={(errorMessage) => {
              if (onError) onError(errorMessage);
            }}
            assignmentData={
              actionValue == BulkAssignActionOption.AssignDoc
                ? bulkAssignStateMap.newAssign
                : null
            }
            onAssignmentDataChange={assignmentDataChangeHandler}
          />
        ),
      },
    ];
  }, [
    actionValue,
    newAssignmentsTreeData,
    nestedAssignTreeData,
    nestedUnassignTreeData,
    startingCount,
    tabValue,
    selectedTreeItemsMap,
    bulkAssignStateMap,
    countLoading,
  ]);
  // ------------------

  const handleActionChange = (value: string) => {
    const action = value as BulkAssignActionOption;
    setActionValue(action);
    setSelectedAction?.(action);

    if (action === BulkAssignActionOption.UnassignDoc) {
      setTabValue(tabSelectionMap.unAssignment);
      onTreeSelect?.(selectedTreeItemsMap.unAssign);
    } else {
      setTabValue(tabSelectionMap.assignment);
      if (tabSelectionMap.assignment == BulkAssignTabOption.ExistingAssign) {
        onTreeSelect?.(selectedTreeItemsMap.assign);
      } else {
        onTreeSelect?.(selectedTreeItemsMap.newAssign);
      }
    }

    if (onActionChange) {
      onActionChange(action);
    }
  };
  // ------------------

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTabValue: number
  ) => {
    if (actionValue == BulkAssignActionOption.AssignDoc) {
      setTabSelectionMap((pv) => {
        return {...tabSelectionMap, assignment: newTabValue};
      });
    } else {
      setTabSelectionMap((pv) => {
        return {...tabSelectionMap, unAssignment: newTabValue};
      });
    }
    setTabValue(newTabValue);
    if (onTabChange) {
      onTabChange(newTabValue);
    }
  };
  // ------------------

  const getTabItem = (tab: ITabOption) => {
    if (actionValue === BulkAssignActionOption.UnassignDoc) {
      return null;
    }
    return (
      <Tab
        key={tab.id}
        id={tab.id}
        label={tab.label}
        aria-controls={`tabpanel-${tab.id}`}
        className="tab typo-text subhead5_size"
        data-testid={tab.id}
      />
    );
  };
  // ------------------

  useEffect(() => {
    if (!isStartingCountLoading && startingCount > 0) {
      if (
        actionValue == BulkAssignActionOption.AssignDoc &&
        tabValue == BulkAssignTabOption.ExistingAssign &&
        bulkAssignStateMap.existingAssign?.samplingMethod.key ==
          BulkAssignSamplingOption.Count
      ) {
        assignmentDataChangeHandler({
          ...bulkAssignStateMap.existingAssign,
          numberToAssign: startingCount.toString(),
        });
      } else if (
        actionValue == BulkAssignActionOption.AssignDoc &&
        tabValue == BulkAssignTabOption.NewAssign &&
        bulkAssignStateMap.newAssign?.samplingMethod.key ==
          BulkAssignSamplingOption.Count
      ) {
        assignmentDataChangeHandler({
          ...bulkAssignStateMap.newAssign,
          numberToAssign: startingCount.toString(),
        });
      }

      setTimeout(() => setCountLoading(false), ASSIGNMENT_DELAY);
    }
  }, [isStartingCountLoading, tabValue]);

  return (
    <Box className="bulk-assign">
      {/* Sort */}
      <Box className="bulk-assign__section">
        <Typography
          component="h3"
          className="typo-text subhead4_size section-title"
          tabIndex={0}
          data-testid="bulk-assign-select-action"
        >
          {t(
            `${enBulkAssign}.documentActionTitle`,
            'Select the action you want to take:'
          )}
        </Typography>
        <Box className="bulk-assign__inputs">
          <Box className="input">
            <ArrRadio
              dataTestId="bulk-assign-actions"
              name="controlled-radio-buttons-group"
              size="xs"
              value={actionValue}
              onChange={handleActionChange}
              data={actions}
              className="actions-radio"
            />
          </Box>
        </Box>
      </Box>
      {/* Tabs */}
      {actionValue !== BulkAssignActionOption.UnassignDoc && (
        <Box className="bulk-assign__tabs">
          <Tabs
            value={tabValue || 0}
            aria-label="tabs"
            className="tabs"
            onChange={handleTabChange}
          >
            {getTabOptions().map((tab) => getTabItem(tab))}
          </Tabs>
        </Box>
      )}
      {/* Tab content */}
      <Box className="bulk-assign__tab-content">
        {/* Tab Panels */}
        {getTabOptions().map((tab, index) => (
          <TabPanel key={tab.id} value={tabValue || 0} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export {BulkAssign};
export default BulkAssign;
